import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'react-toastify';
import { fetchNebulaOverview } from '../../../../services/api/nebula-insights.service';
import type { NebulaDaysFilter, NebulaOverviewData } from '../../../../types/nebula-insights';
import KpiCardsRow, { type KpiCardConfig } from './KpiCardsRow';
import MessageVolumeChart from './MessageVolumeChart';
import ModelUsageChart from './ModelUsageChart';
import TopArtistsByNebula from './TopArtistsByNebula';
import RecentActivityList from './RecentActivityList';
import { formatRecentPeriodRange } from '../utils';

interface OverviewTabProps {
  days: NebulaDaysFilter;
  refreshToken: number;
  onOpenConversation: (conversationId: string) => void;
}

const OverviewTab: React.FC<OverviewTabProps> = ({
  days,
  refreshToken,
  onOpenConversation,
}) => {
  const [data, setData] = useState<NebulaOverviewData | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        const overview = await fetchNebulaOverview(days);
        if (!cancelled) {
          setData(overview);
        }
      } catch (error: any) {
        if (!cancelled) {
          toast.error(error?.message || 'Failed to load Nebula overview');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();
    return () => {
      cancelled = true;
    };
  }, [days, refreshToken]);

  const kpiItems = useMemo<KpiCardConfig[]>(() => {
    if (!data) return [];
    return [
      { key: 'conversations', label: 'Conversations', metric: data.kpis.conversations },
      { key: 'messages', label: 'Messages', metric: data.kpis.messages },
      { key: 'activeArtists', label: 'Active artists', metric: data.kpis.activeArtists },
      {
        key: 'avgMessages',
        label: 'Avg msgs / thread',
        metric: data.kpis.avgMessagesPerConversation,
        format: 'decimal',
      },
    ];
  }, [data]);

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center py-24">
        <div className="w-8 h-8 border-4 border-gray-300 border-t-gray-800 rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6">
      {days === 'recent' && data?.recentPeriod ? (
        <p className="text-xs text-gray-500 font-family-messina-sans">
          KPIs for activity from{' '}
          {formatRecentPeriodRange(data.recentPeriod.start, data.recentPeriod.end)}
          {' '}vs prior period of equal length (derived from latest 7 conversations)
        </p>
      ) : null}

      <KpiCardsRow items={kpiItems} isAllTime={days === 'all'} />

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-4 sm:gap-6">
        <div className="xl:col-span-2">
          <MessageVolumeChart data={data?.messageVolume ?? []} loading={loading} />
        </div>
        <ModelUsageChart data={data?.modelUsage ?? []} loading={loading} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4 sm:gap-6">
        <TopArtistsByNebula artists={data?.topArtists ?? []} />
        <RecentActivityList
          items={data?.recentActivity ?? []}
          onOpenConversation={onOpenConversation}
        />
      </div>
    </div>
  );
};

export default OverviewTab;
